import React, { useCallback, useId } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQuery } from '@tanstack/react-query';
import Select from 'react-select';

import { PropertyAccess as PropertyAccessIcon } from '@/orderForm/OrderForm/OrderFormTitleIcons';
import API from '@/apiRequest';
import AddressSelector from './shared/AddressSelector';
import FieldErrorDisplay from './shared/FieldErrorDisplay';
import { sanitizeAddress } from './shared/sanitizeAddress';
import Radio from '@/components/common/elements/Radio';


const normalizeText = (value = '') =>
  value
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/gi, '')
    .trim()
    .toLowerCase();

const isRealEstateType = (value) => normalizeText(value) === 'na imobiliaria';
const isOtherType = (value) => normalizeText(value) === 'outro';
const shouldShowAddress = (value) => isRealEstateType(value) || isOtherType(value);

const customSelectStyles = (hasError) => ({
  control: (base, state) => ({
    ...base,
    minHeight: '37px',
    height: '37px',
    borderRadius: '4px',
    border: `1px solid ${hasError ? '#dc3545' : state.isFocused ? '#80bdff' : '#ced4da'}`,
    boxShadow: 'none',
    fontFamily: 'Open Sans',
    fontSize: '14px',
    fontWeight: 'normal',
    '&:hover': {
      borderColor: hasError ? '#dc3545' : state.isFocused ? '#80bdff' : '#ced4da',
    },
  }),
  singleValue: base => ({
    ...base,
    fontWeight: 'normal',
    fontSize: '14px',
    color: '#212529',
  }),
  placeholder: base => ({
    ...base,
    fontSize: '14px',
    fontWeight: 'normal',
    color: '#b1b1b1',
  }),
  option: (base, state) => ({
    ...base,
    fontSize: '14px',
    fontWeight: 'normal',
    fontFamily: 'Open Sans',
    backgroundColor: state.isSelected ? '#5882C1' : state.isFocused ? '#f0f4f9' : 'transparent',
    color: state.isSelected ? 'white' : '#374151',
    '&:active': { backgroundColor: '#5882C1' },
  }),
  valueContainer: base => ({
    ...base,
    padding: '0 12px',
    height: '100%',
  }),
  input: base => ({
    ...base,
    margin: 0,
    padding: 0,
    '& input': {
      height: '100% !important',
    },
  }),
  
  indicatorSeparator: () => ({ display: 'none' }),
});

const PropertyAccessField = ({
  formData,
  onChange,
  schema,
  uiSchema,
  errorSchema,
  disabled,
  formContext,
  ...props
}) => {
  formData = formData || {};
  const intl = useIntl();
  const clientId = formContext?.clientId;
  const radioId = useId();

  const hasFieldError = (fieldName) => errorSchema?.[fieldName]?.__errors?.length > 0;

  const { data: clientAddresses = [] } = useQuery({
    queryKey: ['clientAddresses', clientId],
    queryFn: async () => {
      const { data } = await API.get(`/clients/${clientId}/addresses`);
      return Array.isArray(data?.data) ? data.data : [];
    },
    enabled: !!clientId,
  });

  const updateFormData = useCallback(
    (updates) => {
      onChange({ ...formData, ...updates });
    },
    [formData, onChange]
  );

  const handleTypeChange = useCallback(
    (value) => {
      let updates = { type: value };

      if (!shouldShowAddress(value)) {
        updates = { ...updates, address: undefined, realEstateAddress: undefined };
      }

      if (isRealEstateType(value) && clientAddresses.length === 1 && !formData.address?.city) {
        updates = {
          ...updates,
          address: sanitizeAddress(clientAddresses[0].address),
          realEstateAddress: clientAddresses[0].public_id,
        };
      }

      updateFormData(updates);
    },
    [formData, updateFormData, clientAddresses]
  );

  const handleKeysDevolutionTypeChange = useCallback(
    (value) => {
      let updates = { keysDevolutionType: value };

      if (!shouldShowAddress(value)) {
        updates = { ...updates, keysDevolutionAddress: undefined, devolutionRealEstateAddress: undefined };
      }

      if (isRealEstateType(value) && clientAddresses.length === 1 && !formData.keysDevolutionAddress?.city) {
        updates = {
          ...updates,
          keysDevolutionAddress: sanitizeAddress(clientAddresses[0].address),
          devolutionRealEstateAddress: clientAddresses[0].public_id,
        };
      }

      updateFormData(updates);
    },
    [formData, updateFormData, clientAddresses]
  );

  const handleRealEstateAddressChange = useCallback(
    (opt) => {
      if (!opt) {
        updateFormData({ realEstateAddress: undefined, address: undefined });
        return;
      }
      const selected = clientAddresses.find((address) => address.public_id === opt.value);
      if (selected) {
        updateFormData({
          realEstateAddress: opt.value,
          address: sanitizeAddress(selected.address),
        });
      }
    },
    [updateFormData, clientAddresses]
  );

  const handleDevolutionRealEstateAddressChange = useCallback(
    (opt) => {
      if (!opt) {
        updateFormData({ devolutionRealEstateAddress: undefined, keysDevolutionAddress: undefined });
        return;
      }
      const selected = clientAddresses.find((address) => address.public_id === opt.value);
      if (selected) {
        updateFormData({
          devolutionRealEstateAddress: opt.value,
          keysDevolutionAddress: sanitizeAddress(selected.address),
        });
      }
    },
    [updateFormData, clientAddresses]
  );

  const handlePickupAddressChange = useCallback(
    (address) => {
      updateFormData({ address });
    },
    [updateFormData]
  );

  const handleDevolutionAddressChange = useCallback(
    (address) => {
      updateFormData({ keysDevolutionAddress: address });
    },
    [updateFormData]
  );


  const typeOptions = schema.properties?.type?.enum || [];
  const keysDevolutionTypeOptions = schema.properties?.keysDevolutionType?.enum || [];
  const showPickupAddress = shouldShowAddress(formData.type);
  const showDevolutionAddress = shouldShowAddress(formData.keysDevolutionType);
  const titleKey = uiSchema?.['ui:title'] || 'propertyAccess';

  const realEstateSelectValue = formData.realEstateAddress
    ? {
        label: clientAddresses.find(address => address.public_id === formData.realEstateAddress)?.name,
        value: formData.realEstateAddress,
      }
    : null;

  const devolutionRealEstateSelectValue = formData.devolutionRealEstateAddress
    ? {
        label: clientAddresses.find(address => address.public_id === formData.devolutionRealEstateAddress)?.name,
        value: formData.devolutionRealEstateAddress,
      }
    : null;

  return (
    <>
      <div className="mb-[0.6vw] mt-[0.6vw] flex items-center gap-2">
        <div className="flex items-center justify-center shrink-0 w-6 h-6 text-[#393939]">
          <PropertyAccessIcon viewBox="0 0 34.016 34.016" className="w-full h-full fill-current object-contain" />
        </div>
        <span className="font-bold text-[#212529] font-['Open_Sans'] leading-tight" style={{ fontSize: '16px', fontWeight: 700 }}>
          <FormattedMessage id={titleKey} />
        </span>
      </div>

      <div className="row" style={{paddingLeft: '1vw', paddingRight: '1vw'}}>
        <div className="col-md-12 space-y-4">
          {/* Pickup type */}
          <div>
            <div className="mb-1 mt-[0.3vh] inline-block font-['Open_Sans'] text-[12px] font-bold text-[#212529]">
              <FormattedMessage id="keysLocationType" />
            </div>

            <div className="flex flex-wrap items-center gap-x-4">
              {typeOptions.map(option => (
                <div key={option} className="flex flex-row items-center gap-2 mb-2">
                  <Radio
                    name={`propertyAccessType-${radioId}`}
                    id={`propertyAccessType-${radioId}-${option}`}
                    value={option}
                    checked={formData.type === option}
                    onChange={() => handleTypeChange(option)}
                    disabled={disabled}
                  />
                  <label 
                    htmlFor={`propertyAccessType-${radioId}-${option}`}
                    className="m-0 cursor-pointer font-['Open_Sans'] text-[14px] font-normal text-[#212529] leading-none"
                  >
                    <FormattedMessage id={option} />
                  </label>
                </div>
              ))}
            </div>

            <FieldErrorDisplay errorSchema={errorSchema} fieldName="type" />

            {showPickupAddress && (
              <div className="mt-3">
                {isRealEstateType(formData.type) && clientAddresses.length > 1 && (
                  <div className="mb-3 flex flex-col">
                    <label className="mb-1 mt-[0.3vh] inline-block font-['Open_Sans'] text-[12px] font-bold text-[#212529]">
                      <FormattedMessage id="realEstateForKeysLocation" />
                    </label>
                    <Select
                      options={clientAddresses.map(address => ({ label: address.name, value: address.public_id }))}
                      value={realEstateSelectValue}
                      onChange={handleRealEstateAddressChange}
                      isDisabled={disabled}
                      placeholder={intl.formatMessage({ id: 'SELECT' })}
                      styles={customSelectStyles(hasFieldError('realEstateAddress'))}
                    />
                  </div>
                )}

                <AddressSelector
                  addressData={formData.address || {}}
                  onChange={handlePickupAddressChange}
                  disabled={disabled}
                  errors={errorSchema?.address}
                />
              </div>
            )}
          </div>

          {/* Keys devolution type */}
          {schema.properties?.keysDevolutionType && (
            <div>
              <div className="mb-1 mt-[0.3vh] inline-block font-['Open_Sans'] text-[12px] font-bold text-[#212529]">
                <FormattedMessage id="keysDevolutionType" />
              </div>

              <div className="flex flex-wrap items-center gap-x-4">
                {keysDevolutionTypeOptions.map(option => (
                  <div key={option} className="flex flex-row items-center gap-2 mb-2">
                    <Radio
                      name={`keysDevolutionType-${radioId}`}
                      id={`keysDevolutionType-${radioId}-${option}`}
                      value={option}
                      checked={formData.keysDevolutionType === option}
                      onChange={() => handleKeysDevolutionTypeChange(option)}
                      disabled={disabled}
                    />
                    <label 
                      htmlFor={`keysDevolutionType-${radioId}-${option}`}
                      className="m-0 cursor-pointer font-['Open_Sans'] text-[14px] font-normal text-[#212529] leading-none"
                    >
                      <FormattedMessage id={option} />
                    </label>
                  </div>
                ))}
              </div>

              <FieldErrorDisplay errorSchema={errorSchema} fieldName="keysDevolutionType" />

              {showDevolutionAddress && (
                <div className="mt-3">
                  {isRealEstateType(formData.keysDevolutionType) && clientAddresses.length > 1 && (
                    <div className="mb-3 flex flex-col">
                      <label className="mb-1 mt-[0.3vh] inline-block font-['Open_Sans'] text-[12px] font-bold text-[#212529]">
                        <FormattedMessage id="devolutionRealEstateForKeysLocation" />
                      </label>
                       <Select
                        options={clientAddresses.map(address => ({ label: address.name, value: address.public_id }))}
                        value={devolutionRealEstateSelectValue}
                        onChange={handleDevolutionRealEstateAddressChange}
                        isDisabled={disabled}
                        placeholder={intl.formatMessage({ id: 'SELECT' })}
                        styles={customSelectStyles(hasFieldError('devolutionRealEstateAddress'))}
                      />
                    </div>
                  )}

                  <AddressSelector
                    addressData={formData.keysDevolutionAddress || {}}
                    onChange={handleDevolutionAddressChange}
                    disabled={disabled}
                    errors={errorSchema?.keysDevolutionAddress}
                  />
                </div>
              )}
            </div>
          )}

          {/* Keys location */}
          {schema.properties?.keysLocation && (
            <div className="mb-2 flex flex-col">
              <label className="mb-1 mt-[0.3vh] inline-block font-['Open_Sans'] text-[12px] font-bold text-[#212529]">
                <FormattedMessage id="keysLocation" />
              </label>
               <input
                type="text"
                className={`w-full rounded-[4px] !h-[37px] !px-[12px] py-[6px] font-['Open_Sans'] text-[14px] font-normal outline-none transition-all placeholder:text-gray-400 border-[1px] border-solid ${
                  disabled ? 'bg-[#f4f3f4] text-[rgba(0,0,0,0.38)] cursor-not-allowed' : 'bg-white text-[#919191]'
                } ${hasFieldError('keysLocation') ? 'border-[#dc3545] focus:border-[#dc3545]' : 'border-[#ced4da] focus:border-[#80bdff]'}`}
                value={formData.keysLocation || ''}
                onChange={(event) => updateFormData({ keysLocation: event.target.value })}
                disabled={disabled}
                placeholder={intl.formatMessage({ id: 'keysLocation' })}
              />
              <FieldErrorDisplay errorSchema={errorSchema} fieldName="keysLocation" />
            </div>
          )}

          {schema.properties?.accessDetails && (
            <div className="mb-2 flex flex-col">
              <label className="mb-1 mt-[0.3vh] inline-block font-['Open_Sans'] text-[12px] font-bold text-[#212529]">
                <FormattedMessage id="accessDetails" /> *
              </label>
               <textarea
                className={`w-full rounded-[4px] !px-[12px] py-2 font-['Open_Sans'] text-[14px] font-normal outline-none transition-all placeholder:text-gray-400 border-[1px] border-solid ${
                  disabled ? 'bg-[#f4f3f4] text-[rgba(0,0,0,0.38)] cursor-not-allowed' : 'bg-white text-[#919191]'
                } ${hasFieldError('accessDetails') ? 'border-[#dc3545] focus:border-[#dc3545]' : 'border-[#ced4da] focus:border-[#80bdff]'}`}
                rows={3}
                value={formData.accessDetails || ''}
                onChange={(event) => updateFormData({ accessDetails: event.target.value })}
                disabled={disabled}
                placeholder={intl.formatMessage({ id: 'accessDetails' })}
              />
              <FieldErrorDisplay errorSchema={errorSchema} fieldName="accessDetails" />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

PropertyAccessField.propTypes = {
  formData: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  schema: PropTypes.object,
  errorSchema: PropTypes.object,
  disabled: PropTypes.bool,
  formContext: PropTypes.object,
  rootFormData: PropTypes.object,
  uiSchema: PropTypes.object,
};

export default PropertyAccessField;
